// @flow

import React, { Fragment } from 'react';
import type { Node } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { TickIcon } from '@riseart/icons';
import { Link } from '@riseart/common';
import { FilterService } from '@riseart/filter-service';
import { Section, Wrapper } from '@riseart/layout';
import { ArtActionInfo, ArtActionInfoCommon, ArtDetails, ArtDetailsItem } from '@riseart/art';
import { filter as FILTER_ENUM, meta as META_ENUM } from 'Enum';
import { LDTYPES, generateLDJSON } from 'shared_models/seo/ldJSON';
import { generateHrefLangParams } from 'shared_services/riseart/meta/utils';
import { MetaProvider } from 'shared_data/providers/meta/Meta';
import { useLocale } from 'shared_services/redux/hooks/useLocale';
import { LocationManager } from 'shared_services/riseart/url/Location';
import { renderNotFoundArtImage } from 'shared_services/riseart/utils/artDataUtils';
import { UrlAssembler } from 'shared_services/riseart/utils/UrlAssembler';
import { NotFoundContent } from 'shared_components/common/notfound/Content';
import { LocalizedConfig } from 'shared_services/riseart/utils/LocalizedConfig';
import { FilterBreadcrumbs } from 'shared_components/common/filters/breadcrumbs/Breadcrumbs';
import { getFilterDomainConfig } from 'shared_models/configs/filters/domains';
import { generateSrcForCDN } from 'shared_components/common/artdirection/picture/Picture';

type Props = {
  data: Object,
  metaSubscriber: string,
};

/**
 * NotFoundArt
 *
 * @param {Props} props
 * @returns {Node}
 */
export function NotFoundArt({ data, metaSubscriber }: Props): Node {
  const { name: localeCode } = useLocale();
  const origin = LocationManager.get('origin');
  const { formatMessage } = useIntl();
  const location = useLocation();

  if (!data) {
    return null;
  }

  const filterService = new FilterService(
    getFilterDomainConfig(FILTER_ENUM.domain.ART),
    formatMessage,
  );
  const learnMoreLabel = formatMessage({ id: 'common.learnMore' });
  const image = data.image
    ? renderNotFoundArtImage({ type: data.image.type, ...data.image.file }, data.title)
    : null;
  const slug = (data.slug && data.slug[localeCode]) || null;
  const artistUrl = UrlAssembler.artistProfile(data.artist.id, data.artist.alias);
  const metaData = { name: data.title, artist: data.artist.name };
  const canonicalURL = UrlAssembler.artDetail(data.id, slug, { origin });
  const hrefLangParams =
    data && data.slug
      ? generateHrefLangParams(
          Object.keys(data.slug).map((key) => ({
            localeCode: key,
            slug: data.slug[key],
          })),
        )
      : null;

  return (
    <Fragment>
      <Section padding="none">
        <Wrapper>
          <MetaProvider
            metaSubscriber={metaSubscriber}
            meta={{
              [META_ENUM.METATYPE.LINK_CANONICAL]: canonicalURL,
              [META_ENUM.METATYPE.LINK_CANONICAL_REDIRECT]: UrlAssembler.artDetail(data.id, slug, {
                ...(location.search ? { search: location.search } : null),
                ...(location.hash ? { hash: location.hash } : null),
              }),
              [META_ENUM.METATYPE.LINK_HREFLANG]: hrefLangParams,
              [META_ENUM.METATYPE.LINK_HREFLANG_REDIRECT]: hrefLangParams,
              [META_ENUM.METATYPE.META_TITLE]: formatMessage(
                { id: 'meta.artDetails.dynamicMetaTitle' },
                metaData,
              ),
              [META_ENUM.METATYPE.META_DESCRIPTION]: formatMessage(
                { id: 'meta.artDetails.dynamicMetaDescription' },
                metaData,
              ),
              [META_ENUM.METATYPE.META_KEYWORDS]: formatMessage({ id: 'meta.artDetails.keywords' }),
              [META_ENUM.METATYPE.PAGE_TITLE]: formatMessage(
                { id: 'meta.artDetails.dynamicPageTitle' },
                metaData,
              ),
              [META_ENUM.METATYPE.META_ROBOTS]: META_ENUM.ATTRIBUTE_CONTENT.NO_INDEX,
              structuredDataArtwork: generateLDJSON(LDTYPES.ARTWORK, {
                ...data,
                artistName: (data && data.artist && data.artist.name) || null,
                artistUrl:
                  (data &&
                    data.artist &&
                    UrlAssembler.artistProfile(data.artist.id, data.artist.alias, { origin })) ||
                  null,
                mediumKey: data.medium,
                medium: formatMessage({ id: `filters.art.medium.${data.medium}` }),
                url: canonicalURL,
                image: data.image ? generateSrcForCDN({ ...data.image.file, width: 1200 }) : null,
                mainEntityOfPage: {
                  type: 'WebPage',
                  url: canonicalURL,
                },
              }),
            }}
          />
          <FilterBreadcrumbs
            includePageRootLink={false}
            includeMediumInStyle
            filterDomain={FILTER_ENUM.domain.ART}
            filters={{
              medium: data.medium ? [data.medium] : null,
              style: data.style ? [data.style] : null,
            }}
            config={['medium', 'style']}
            additionalLinks={[
              ...(data.artist
                ? [
                    {
                      isLink: data.artist.active,
                      name: data.artist.name,
                      url: artistUrl,
                    },
                  ]
                : []),
              {
                isLink: false,
                name: data.title,
                url: LocationManager.get('pathname'),
              },
            ]}
          />
        </Wrapper>
      </Section>
      <NotFoundContent
        image={image}
        title={data.title}
        subtitle={
          data.artist
            ? formatMessage(
                { id: 'common.byNameLowercase' },
                {
                  name:
                    data.artist && data.artist.active ? (
                      <Link to={artistUrl} title={data.artist.name}>
                        {data.artist.name}
                      </Link>
                    ) : (
                      (data.artist && data.artist.name) || null
                    ),
                },
              )
            : null
        }
        content={
          <ArtDetails nodeType="ul">
            <ArtDetailsItem
              key="medium"
              separator=":&nbsp;&nbsp;"
              name={formatMessage({ id: `components.filters.medium.label` })}
            >
              <Link
                to={filterService.assemble({ medium: data.medium }, {})}
                title={formatMessage({ id: `filters.art.medium.${data.medium}` })}
              >
                {formatMessage({ id: `filters.art.medium.${data.medium}` })}
              </Link>
            </ArtDetailsItem>
            <ArtDetailsItem
              key="subject"
              separator=":&nbsp;&nbsp;"
              name={formatMessage({ id: 'components.filters.subject.label' })}
            >
              <Link
                to={filterService.assemble({ subject: data.subject }, {})}
                title={formatMessage({ id: `filters.art.subject.${data.subject}` })}
              >
                {formatMessage({ id: `filters.art.subject.${data.subject}` })}
              </Link>
            </ArtDetailsItem>
            <ArtDetailsItem
              key="style"
              separator=":&nbsp;&nbsp;"
              name={formatMessage({ id: 'components.filters.style.label' })}
            >
              <Link
                to={filterService.assemble({ style: data.style }, {})}
                title={formatMessage({ id: `filters.art.style.${data.style}` })}
              >
                {formatMessage({ id: `filters.art.style.${data.style}` })}
              </Link>
            </ArtDetailsItem>
          </ArtDetails>
        }
        message={<strong>{formatMessage({ id: 'components.art.unavailable' })}</strong>}
        footer={
          <ArtActionInfo>
            <ArtActionInfoCommon icon={<TickIcon />}>
              {formatMessage({ id: 'components.art.returnsLabel' })}{' '}
              <Link
                title={learnMoreLabel}
                external
                target="_blank"
                href={LocalizedConfig.get('externalUrls.freeReturns')}
              >
                {learnMoreLabel}
              </Link>
            </ArtActionInfoCommon>
            <ArtActionInfoCommon icon={<TickIcon />}>
              {formatMessage({ id: 'components.art.worldwideShipping' })}{' '}
              <Link
                title={learnMoreLabel}
                external
                target="_blank"
                href={LocalizedConfig.get('externalUrls.shippingHelp')}
              >
                {learnMoreLabel}
              </Link>
            </ArtActionInfoCommon>
          </ArtActionInfo>
        }
      />
    </Fragment>
  );
}
